import { LoaderCircle, X } from "lucide-react";
import React, { useEffect, useMemo, useRef } from "react";
import type { CumulativeSeriesPoint } from "../../components/performanceChartV3Utils";
import { DEFAULT_BENCHMARK_SYMBOL, getBenchmarkBySymbol } from "../../config/benchmarkContracts";
import { PLUTUS_MANAGED_LABEL } from "../../contexts/AlphaAllocationDraftContext";
import { useAnalyzerRiskAnalysis } from "../../hooks/useAnalyzerRiskAnalysis";
import type { FactorEntry } from "../../services/productFactorsService";
import { formatUtcDate } from "../../utils/dateUtils";
import {
  formatCurrencyWithCommas,
  formatPercentage,
  formatSignedPercentage,
  formatWeight,
} from "../../utils/numberUtils";
import DetailNetBenchmarkChart from "../dashboard/components/DetailNetBenchmarkChart";
import {
  buildAlphaInvestmentValueSeries,
  type AlphaManagedStrategyPerformanceData,
} from "./alphaRecommendationData";
import type { AlphaManagedPerf } from "./useAlphaManagedSizing";

const ALPHA_BENCHMARK = getBenchmarkBySymbol(DEFAULT_BENCHMARK_SYMBOL);
const CHART_HEIGHT = 240;
const EMPTY_FACTORS: FactorEntry[] = [];

export interface AlphaManagedConstituent {
  entityType: "PORTFOLIO" | "BASKET";
  productId: number;
  name: string;
  weightPct: number;
}

export interface AlphaManagedPreviewModalProps {
  open: boolean;
  onClose: () => void;
  weightPct: number;
  amountUsd: number | null;
  strategyName: string;
  perf: AlphaManagedPerf;
  managedChangePending: boolean;
  rematchPending: boolean;
  heldSameWindowReturnPct: number | null;
  blockedReason: string | null;
  constituents?: ReadonlyArray<AlphaManagedConstituent> | null;
  constituentsLoading?: boolean;
}

function toPercentSeries(
  data: AlphaManagedStrategyPerformanceData,
  factors: FactorEntry[],
  amountUsd: number
): CumulativeSeriesPoint[] {
  return buildAlphaInvestmentValueSeries(factors, data.window, amountUsd).map((point) => {
    const iso = new Date(point.timestamp).toISOString();
    return {
      date: iso,
      fullDate: iso,
      performance: (point.value / amountUsd - 1) * 100,
    };
  });
}

function windowLabel(ms: number): string {
  return formatUtcDate(new Date(ms).toISOString(), { month: "short", day: "numeric" });
}

// Read-only drill-in for the exact Managed sleeve. It preserves the existing
// performance view and adds current allocation, constituents, and review
// context without introducing a second editing workflow.
const AlphaManagedPreviewModal: React.FC<AlphaManagedPreviewModalProps> = ({
  open,
  onClose,
  weightPct,
  amountUsd,
  strategyName,
  perf,
  managedChangePending,
  rematchPending,
  heldSameWindowReturnPct,
  blockedReason,
  constituents,
  constituentsLoading = false,
}) => {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const data = perf.status === "ready" ? perf.data : null;
  const chartAmountUsd = amountUsd != null && amountUsd > 0 ? amountUsd : null;

  const { metrics, benchmarkMetrics } = useAnalyzerRiskAnalysis(
    data?.strategyFactors ?? EMPTY_FACTORS,
    data?.benchmarkFactors ?? EMPTY_FACTORS
  );

  const strategySeries = useMemo(
    () => (data && chartAmountUsd != null
      ? toPercentSeries(data, data.strategyFactors, chartAmountUsd)
      : []),
    [data, chartAmountUsd]
  );
  const benchmarkSeries = useMemo(
    () => (data && chartAmountUsd != null
      ? toPercentSeries(data, data.benchmarkFactors, chartAmountUsd)
      : []),
    [data, chartAmountUsd]
  );

  const detailChart = useMemo(
    () => ({
      loading: perf.status === "loading",
      error: null,
      chartHeight: CHART_HEIGHT,
      chartData: strategySeries,
      selectedPeriod: "All",
      entityType: "MANAGED_STRATEGY" as const,
    }),
    [perf.status, strategySeries]
  );
  const providedBenchmarkSeries = useMemo(
    () => (ALPHA_BENCHMARK
      ? [{ benchmark: ALPHA_BENCHMARK, points: benchmarkSeries }]
      : []),
    [benchmarkSeries]
  );

  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (open) headingRef.current?.focus();
  }, [open]);

  if (!open) return null;

  const isPerformanceReady = data != null
    && chartAmountUsd != null
    && strategySeries.length > 1
    && benchmarkSeries.length > 1;
  const startMs = data ? data.window.requestedStartMs ?? data.window.startMs : null;
  const endingValue = data && chartAmountUsd != null
    ? chartAmountUsd * (1 + data.strategyReturnPct / 100)
    : null;
  const benchmarkEndingValue = data && chartAmountUsd != null
    ? chartAmountUsd * (1 + data.benchmarkReturnPct / 100)
    : null;
  const performanceTiles: ReadonlyArray<{ label: string; value: string; note: string }> = data
    ? [
      {
        label: "Return",
        value: formatSignedPercentage(data.strategyReturnPct, 1),
        note: `S&P ${formatSignedPercentage(data.benchmarkReturnPct, 1)}`,
      },
      {
        label: "Max drawdown",
        value: metrics?.maxDrawdown != null ? formatPercentage(metrics.maxDrawdown, 1) : "--",
        note: `S&P ${benchmarkMetrics?.maxDrawdown != null ? formatPercentage(benchmarkMetrics.maxDrawdown, 1) : "--"}`,
      },
      {
        label: "Volatility",
        value: metrics?.volatility != null ? formatPercentage(metrics.volatility, 1) : "--",
        note: `S&P ${benchmarkMetrics?.volatility != null ? formatPercentage(benchmarkMetrics.volatility, 1) : "--"}`,
      },
    ]
    : [];

  const changeExplanation = rematchPending
    ? "This amount changes the strategy matched to your profile. Applying may buy and sell holdings inside Plutus Managed to move to the newly matched strategy."
    : managedChangePending
      ? "This changes how much of the account Plutus manages. Applying may buy or sell holdings to rebalance the managed sleeve and your other allocations."
      : "Changing this percentage changes how much of the account Plutus manages and may cause buying or selling during a rebalance.";

  const reviewExplanation = blockedReason != null
    ? `Action required before review. ${blockedReason}`
    : rematchPending
      ? "Review required. The allocation review will call out the managed strategy update before anything is applied."
      : managedChangePending
        ? "Review required. The allocation review will show the managed percentage change before anything is applied."
        : "No managed allocation change is waiting for review.";

  return (
    <div
      className="fixed inset-0 z-50 flex animate-[alphaOverlayIn_0.2s_ease-out] bg-v7-ink/60 motion-reduce:animate-none sm:items-center sm:justify-center sm:p-4"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="alpha-managed-details-title"
        data-testid="alpha-managed-preview-modal"
        className="flex h-full w-full flex-col overflow-hidden bg-v7-white font-v7-sans animate-[alphaModalIn_0.24s_cubic-bezier(0.4,0,0.2,1)] motion-reduce:animate-none sm:h-auto sm:max-h-[92vh] sm:w-[92vw] sm:max-w-[560px] sm:rounded-v7-popover sm:border sm:border-v7-ink/10 sm:shadow-[0_24px_60px_rgba(17,29,50,0.35)]"
      >
        <div className="flex items-start justify-between gap-4 border-b border-v7-ink/12 px-5 pb-4 pt-[calc(1.25rem+env(safe-area-inset-top))] sm:px-6 sm:pt-5">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-v7-bronze-dark">
              {PLUTUS_MANAGED_LABEL}
            </p>
            <h2
              ref={headingRef}
              tabIndex={-1}
              id="alpha-managed-details-title"
              className="mt-1 font-v7-serif text-2xl font-normal tracking-[-0.01em] text-v7-ink focus:outline-none"
            >
              Managed allocation details
            </h2>
            <p className="mt-1 text-[13px] text-v7-ink-soft">Read-only context for this allocation</p>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="grid h-11 w-11 shrink-0 place-items-center rounded-v7-control border border-v7-ink/15 text-v7-ink-soft transition-colors hover:border-v7-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-bronze-dark"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5 sm:px-6 sm:py-6" aria-live="polite">
          <dl className="divide-y divide-v7-ink/12 border-y border-v7-ink/15">
            <div className="flex items-baseline justify-between gap-4 py-4">
              <dt className="text-[11px] font-medium uppercase tracking-[0.1em] text-v7-ink/50">
                Current allocation
              </dt>
              <dd className="font-v7-serif text-lg text-v7-ink">
                {formatWeight(weightPct)}%
              </dd>
            </div>
            <div className="flex items-baseline justify-between gap-4 py-4">
              <dt className="text-[11px] font-medium uppercase tracking-[0.1em] text-v7-ink/50">
                Managed amount
              </dt>
              <dd className="font-v7-serif text-lg text-v7-ink">
                {amountUsd == null ? "Unavailable" : formatCurrencyWithCommas(amountUsd, 0)}
              </dd>
            </div>
            <div className="flex items-start justify-between gap-6 py-4">
              <dt className="text-[11px] font-medium uppercase tracking-[0.1em] text-v7-ink/50">
                Selected strategy
              </dt>
              <dd className="max-w-[15rem] text-right">
                <span className="block text-[15px] font-medium text-v7-ink">{strategyName}</span>
                <span className="mt-0.5 block text-xs leading-[17px] text-v7-ink-soft">
                  Matched to your profile and managed amount
                </span>
              </dd>
            </div>
          </dl>

          <section
            className="border-b border-v7-ink/15 py-5"
            aria-labelledby="alpha-managed-performance-heading"
          >
            <h3 id="alpha-managed-performance-heading" className="text-[15px] font-medium text-v7-ink">
              Performance at this amount
            </h3>
            {perf.status === "loading" ? (
              <div
                role="status"
                className="flex items-center justify-center gap-2 py-16 text-sm text-v7-ink-soft"
              >
                <LoaderCircle className="h-5 w-5 animate-spin text-v7-bronze-dark" aria-hidden="true" />
                Loading performance
              </div>
            ) : isPerformanceReady && data && startMs != null && chartAmountUsd != null ? (
              <>
                <div
                  className="mt-3 overflow-hidden rounded-v7-panel border border-v7-ink/15 bg-v7-white"
                  aria-label="Managed mix performance at this amount"
                >
                  <div className="px-4 pt-4">
                    <p className="font-v7-serif text-lg leading-tight text-v7-ink">
                      {formatCurrencyWithCommas(chartAmountUsd, 0)} on {windowLabel(startMs)} would be{" "}
                      <span className="text-v7-bronze-dark">
                        {endingValue == null ? "--" : formatCurrencyWithCommas(endingValue, 0)}
                      </span>{" "}
                      as of {windowLabel(data.window.endMs)}
                    </p>
                    <p className="mt-1 text-xs text-v7-ink-soft">
                      vs {benchmarkEndingValue == null ? "--" : formatCurrencyWithCommas(benchmarkEndingValue, 0)} in the S&amp;P 500
                    </p>
                  </div>
                  <div className="px-2 pb-3 pt-2">
                    <DetailNetBenchmarkChart
                      chart={detailChart}
                      benchmarks={ALPHA_BENCHMARK ? [ALPHA_BENCHMARK] : []}
                      providedBenchmarkSeries={providedBenchmarkSeries}
                      compact
                      showNet={false}
                      showPeriodFilters={false}
                      showWatermark={false}
                      isDarkMode={false}
                      useUtcDateLabels
                      productLabel={PLUTUS_MANAGED_LABEL}
                      ariaLabel={`${PLUTUS_MANAGED_LABEL} cumulative return ${formatSignedPercentage(data.strategyReturnPct, 1)} versus the S&P 500 ${formatSignedPercentage(data.benchmarkReturnPct, 1)}`}
                    />
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-3 gap-px overflow-hidden rounded-v7-panel border border-v7-ink/15 bg-v7-ink/10">
                  {performanceTiles.map((tile) => (
                    <div key={tile.label} className="bg-v7-white p-3">
                      <p className="text-[9px] font-semibold uppercase tracking-[0.07em] text-v7-ink/50">
                        {tile.label}
                      </p>
                      <p className="mt-1 font-v7-serif text-lg text-v7-ink">{tile.value}</p>
                      <p className="mt-0.5 text-[10px] text-v7-ink-soft">{tile.note}</p>
                    </div>
                  ))}
                </div>

                {rematchPending && heldSameWindowReturnPct != null && (
                  <p
                    data-testid="alpha-managed-preview-performance-context"
                    className="mt-4 flex items-start gap-2 text-xs leading-relaxed text-v7-bronze-dark"
                  >
                    <span aria-hidden="true" className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-v7-bronze-dark" />
                    You are switching mixes: your current mix returned{" "}
                    {formatSignedPercentage(heldSameWindowReturnPct, 1)} over the same window.
                  </p>
                )}
              </>
            ) : (
              <p className="mt-3 rounded-v7-control border border-v7-ink/15 bg-v7-paper px-4 py-3 text-sm text-v7-ink-soft">
                Model performance isn&apos;t available right now. You can still size and apply
                your allocation.
              </p>
            )}

            <p className="mt-4 text-[11px] leading-relaxed text-v7-ink/50">
              Model performance of the mix matched to this amount. Model results do not include
              fees or trading costs and are not a guarantee of future returns.
            </p>
          </section>

          <section className="border-b border-v7-ink/15 py-5" aria-labelledby="alpha-managed-change-heading">
            <h3 id="alpha-managed-change-heading" className="text-[15px] font-medium text-v7-ink">
              What changing the amount means
            </h3>
            <p className="mt-2 text-[13px] leading-[20px] text-v7-ink-soft">
              {changeExplanation}
            </p>
          </section>

          <section className="border-b border-v7-ink/15 py-5" aria-labelledby="alpha-managed-review-heading">
            <h3 id="alpha-managed-review-heading" className="text-[15px] font-medium text-v7-ink">
              Review before applying
            </h3>
            <p
              data-testid="alpha-managed-preview-context"
              className={`mt-2 border-l-2 pl-3 text-[13px] leading-[20px] ${
                blockedReason != null || rematchPending
                  ? "border-v7-bronze-dark text-v7-ink"
                  : "border-v7-ink/20 text-v7-ink-soft"
              }`}
            >
              {reviewExplanation}
            </p>
          </section>

          {constituents !== undefined && (
            <section
              data-testid="alpha-managed-constituent-mix"
              className="py-5"
              aria-labelledby="alpha-managed-constituent-heading"
            >
              <h3
                id="alpha-managed-constituent-heading"
                className="text-[13px] font-medium text-v7-ink-soft"
              >
                Constituent mix breakdown
              </h3>
              {constituentsLoading ? (
                <p role="status" className="mt-2 text-[13px] text-v7-ink-soft">
                  Loading constituent mix
                </p>
              ) : constituents && constituents.length > 0 ? (
                <dl className="mt-3 divide-y divide-v7-ink/10 rounded-v7-control bg-v7-paper px-4">
                  {constituents.map((constituent) => (
                    <div
                      key={`${constituent.entityType}-${constituent.productId}`}
                      className="flex items-baseline justify-between gap-4 py-2.5"
                    >
                      <dt className="min-w-0 truncate text-[13px] text-v7-ink-soft">
                        {constituent.name}
                      </dt>
                      <dd className="shrink-0 font-v7-serif text-[15px] tabular-nums text-v7-ink">
                        {formatWeight(constituent.weightPct)}%
                      </dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <p className="mt-2 text-[13px] text-v7-ink-soft">
                  Constituent mix is unavailable.
                </p>
              )}
            </section>
          )}
        </div>

        <div className="flex items-center justify-end border-t border-v7-ink/12 px-5 py-4 sm:px-6">
          <button
            type="button"
            onClick={onClose}
            className="min-h-11 shrink-0 rounded-v7-control bg-v7-ink px-[18px] text-[13px] font-medium text-v7-white transition-colors hover:bg-v7-ink-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-bronze-dark"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default AlphaManagedPreviewModal;
